import { useState, useRef, useEffect } from "react";
import {
  Activity,
  ActivityType,
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_ORDER,
  BookingStatus,
  BOOKING_STATUS,
  BOOKING_ORDER,
  hasRoute,
  isJourney,
  allTrailsSearchUrl,
  parseAllTrailsShare,
  addDaysISO,
  emptyActivity,
  newId,
} from "./plannerTypes";
import { geocode, GeoResult, GeoBias } from "./geocode";
import { lookupFlight } from "./flightLookup";

interface Props {
  initial?: Activity; // present => editing an existing card
  defaultDay?: number | null; // starting day when adding a new card
  dayCount: number; // how many days are available to assign to
  showDaySelect?: boolean; // hide the day picker for new ideas (pool only)
  multiDay?: boolean; // adding: pick several days → one card per day
  region?: GeoBias; // trip region (base coords) — anchors AllTrails geocoding
  tripStartISO?: string; // trip start date — used to date a flight lookup
  onSave: (activity: Activity) => void;
  onSaveMany?: (activities: Activity[]) => void; // used in multiDay mode
  onCancel: () => void;
}

// Build geocode query candidates for an AllTrails place name, tried in order
// until one resolves. AllTrails mixes English descriptors with proper names, so
// we translate the common ones (Hut→Rifugio etc.) and strip the rest.
const GENERIC = /\b(panoramic|panorama|scenic|viewpoint|overlook|view|hut|refuge|shelter|trailhead|trail|hike|loop|circuit|the|and|via|point|summit|waterfall|falls)\b/gi;
const geoCandidates = (raw: string): string[] => {
  const q = raw.trim();
  const out = [q];
  const clean = (s: string) => s.replace(GENERIC, " ").replace(/\s+/g, " ").trim();
  if (/\b(hut|refuge)\b/i.test(q)) out.push(("Rifugio " + clean(q)).trim());
  if (/\blake\b/i.test(q)) out.push(("Lago " + clean(q)).trim());
  if (/\b(mount|mt)\b/i.test(q)) out.push(("Monte " + clean(q)).trim());
  const stripped = clean(q);
  if (stripped && stripped !== q) out.push(stripped);
  const words = (stripped || q).split(/\s+/).filter((w) => w.length >= 4);
  if (words.length) out.push(words[words.length - 1]); // salient token, last resort
  return out.filter((v, i, a) => v && a.indexOf(v) === i);
};

export interface LocationProps {
  value: string;
  hasCoords: boolean;
  placeholder: string;
  bias?: GeoBias; // bias + distance-sort results toward this point (hike end)
  onText: (text: string) => void; // typing (clears coords)
  onPick: (r: GeoResult) => void; // chose a suggestion
}

// A single autocomplete location input (Photon geocoder, debounced).
export const LocationAutocomplete = ({ value, hasCoords, placeholder, bias, onText, onPick }: LocationProps) => {
  const [suggestions, setSuggestions] = useState<GeoResult[]>([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emptyResult, setEmptyResult] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    abortRef.current?.abort();
  }, []);

  const onType = (v: string) => {
    onText(v);
    setShow(true);
    if (timer.current) window.clearTimeout(timer.current);
    if (v.trim().length < 3) { setSuggestions([]); setLoading(false); setEmptyResult(false); return; }
    setLoading(true);
    timer.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const results = await geocode(v.trim(), ctrl.signal, bias);
        setSuggestions(results);
        setEmptyResult(results.length === 0);
      } catch { /* aborted or network error */ }
      setLoading(false);
    }, 400);
  };

  const choose = (r: GeoResult) => { onPick(r); setSuggestions([]); setShow(false); };

  return (
    <div className="planner__autocomplete">
      <input
        className="planner__input"
        value={value}
        onChange={(e) => onType(e.target.value)}
        onFocus={() => suggestions.length > 0 && setShow(true)}
        onBlur={() => window.setTimeout(() => setShow(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {hasCoords && <span className="planner__geo-ok" title="Plats vald">📍</span>}
      {show && (loading || suggestions.length > 0 || (bias && emptyResult)) && (
        <ul className="planner__suggestions">
          {loading && <li className="planner__suggestion planner__suggestion--muted">Söker…</li>}
          {!loading && bias && emptyResult && suggestions.length === 0 && (
            <li className="planner__suggestion planner__suggestion--muted">
              Inga träffar nära start – prova platsens lokala namn (t.ex. "Sorapis" i stället för "Lake Sorapis")
            </li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={i}
              className="planner__suggestion"
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
            >
              {s.label}
              {s.distanceKm != null && (
                <span className="planner__suggestion-dist">
                  {s.distanceKm < 10 ? s.distanceKm.toFixed(1) : Math.round(s.distanceKm)} km
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Inline form used both for adding a new activity and editing an existing one.
export const ActivityForm = ({ initial, defaultDay = null, dayCount, showDaySelect = true, multiDay = false, region, tripStartISO, onSave, onSaveMany, onCancel }: Props) => {
  const [draft, setDraft] = useState<Activity>(
    initial ?? { ...emptyActivity(), day: defaultDay }
  );

  // multiDay mode: which days the new card should be copied onto (empty = idea pool).
  const [days, setDays] = useState<number[]>(defaultDay != null ? [defaultDay] : []);
  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b)));

  // Edit mode: extend a dated card across N consecutive days (1 = just this day).
  // Kept as free text so the field can be cleared while typing; clamped on blur/use.
  const [durationInput, setDurationInput] = useState(String(initial?.span && initial.span > 1 ? initial.span : 1));
  const spanMax = draft.day != null ? dayCount - draft.day : 1;
  const duration = Math.max(1, Math.min(spanMax, parseInt(durationInput, 10) || 1));

  const [atHint, setAtHint] = useState("");
  const [flightBusy, setFlightBusy] = useState(false);
  const [flightMsg, setFlightMsg] = useState("");
  const set = (patch: Partial<Activity>) => setDraft((d) => ({ ...d, ...patch }));

  // Pull airports/times from the flight number via the worker (AeroDataBox).
  const fetchFlight = async () => {
    const number = (draft.flightNumber || "").trim();
    if (!number) return;
    const date =
      draft.day != null && tripStartISO ? addDaysISO(tripStartISO, draft.day) : tripStartISO;
    if (!date) { setFlightMsg("Lägg flyget på en dag först (för datum)."); return; }
    setFlightBusy(true);
    setFlightMsg("");
    try {
      const f = await lookupFlight(number, date);
      if (!f) { setFlightMsg("Hittade inget flyg för det numret/datumet."); }
      else {
        setDraft((d) => ({
          ...d,
          title: d.title.trim() ? d.title : [f.airline, f.number].filter(Boolean).join(" "),
          location: f.departure.name || d.location,
          lat: f.departure.lat,
          lng: f.departure.lng,
          endLocation: f.arrival.name || d.endLocation,
          endLat: f.arrival.lat,
          endLng: f.arrival.lng,
          time: f.departure.timeLocal || d.time,
          arrTime: f.arrival.timeLocal || d.arrTime,
        }));
        setFlightMsg(
          `✓ ${f.departure.name} ${f.departure.timeLocal || ""} → ${f.arrival.name} ${f.arrival.timeLocal || ""}`.trim()
        );
      }
    } catch (e: any) {
      setFlightMsg(e?.message || "Kunde inte hämta flyg.");
    }
    setFlightBusy(false);
  };

  // Geocode an AllTrails place name, trying translated/stripped candidates until
  // one resolves (biased to the trip region / hike start to avoid global junk).
  const bestGeo = async (query: string, bias?: GeoBias): Promise<GeoResult | undefined> => {
    for (const v of geoCandidates(query)) {
      const r = await geocode(v, undefined, bias);
      if (r[0]) return r[0];
    }
    return undefined;
  };

  // If a pasted value is an AllTrails share, lift out the link + trail name and
  // auto-fill start/end places (geocoded → map pins). Returns true if handled.
  const tryAllTrails = (value: string): boolean => {
    const parsed = parseAllTrailsShare(value);
    if (!parsed) return false;
    setDraft((d) => ({
      ...d,
      type: "hike",
      link: parsed.url,
      title: d.title.trim() ? d.title : parsed.title,
    }));
    setAtHint("Hämtade från AllTrails – kontrollera plats/mål nedan.");
    (async () => {
      // Resolve the goal first (its proper name usually geocodes cleanly), then
      // anchor the start (often a vague English "X Hut") to the goal's location.
      const e = parsed.end ? await bestGeo(parsed.end, region) : undefined;
      if (e) setDraft((d) => ({ ...d, endLocation: e.label, endLat: e.lat, endLng: e.lng }));
      const anchor: GeoBias | undefined = e ? { lat: e.lat, lng: e.lng } : region;
      const s = parsed.start ? await bestGeo(parsed.start, anchor) : undefined;
      if (s) setDraft((d) => ({ ...d, location: s.label, lat: s.lat, lng: s.lng }));
    })();
    return true;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    let title = draft.title.trim();
    // Journeys can skip the title: fall back to "Start → Slut", using a short
    // form of each place (name before the first comma, max two words).
    if (!title && isJourney(draft.type)) {
      const shortPlace = (s: string) =>
        s.split(",")[0].trim().split(/\s+/).slice(0, 2).join(" ");
      const from = shortPlace(draft.location);
      const to = shortPlace(draft.endLocation);
      if (from || to) title = [from || "?", to || "?"].join(" → ");
    }
    if (!title) return;
    const base = { ...draft, title };
    if (multiDay && onSaveMany) {
      // one independent card per selected day (fresh id each); none → idea pool.
      const targets = days.length ? days : [null];
      onSaveMany(targets.map((d) => ({ ...base, id: newId(), day: d })));
      return;
    }
    // A dated card carries its length as one continuous activity (span days);
    // idea-pool cards (no day) are always single.
    onSave({ ...base, span: draft.day != null ? duration : 1 });
  };

  const isHike = draft.type === "hike";
  const routed = hasRoute(draft.type); // hike or travel: has a start + end

  return (
    <form className="planner__form" onSubmit={submit}>
      <input
        className="planner__input"
        value={draft.title}
        onChange={(e) => { if (!tryAllTrails(e.target.value)) { set({ title: e.target.value }); setAtHint(""); } }}
        placeholder={
          isJourney(draft.type)
            ? "Titel (valfritt — annars Start → Slut)"
            : "Vad ska ni göra? (eller klistra in en AllTrails-länk)"
        }
        autoFocus
      />
      {atHint && <span className="planner__at-hint">🥾 {atHint}</span>}
      <div className="planner__form-row">
        <select
          className="planner__select"
          value={draft.type}
          onChange={(e) => set({ type: e.target.value as ActivityType })}
        >
          {ACTIVITY_TYPE_ORDER.map((t) => (
            <option key={t} value={t}>
              {ACTIVITY_TYPES[t].icon} {ACTIVITY_TYPES[t].label}
            </option>
          ))}
        </select>
        <input
          className="planner__input"
          value={draft.time}
          onChange={(e) => set({ time: e.target.value })}
          placeholder="Tid (t.ex. 09:00)"
        />
      </div>
      <LocationAutocomplete
        value={draft.location}
        hasCoords={draft.lat !== null}
        placeholder={routed ? "Startplats (autocomplete)" : "Plats (autocomplete)"}
        onText={(t) => set({ location: t, lat: null, lng: null })}
        onPick={(r) => set({ location: r.label, lat: r.lat, lng: r.lng })}
      />
      {routed && (
        <LocationAutocomplete
          value={draft.endLocation}
          hasCoords={draft.endLat !== null}
          placeholder="Slutplats (autocomplete)"
          // proximity limit only makes sense for hikes (end near the trailhead);
          // drives/flights can go anywhere, so search globally.
          bias={isHike && draft.lat !== null && draft.lng !== null ? { lat: draft.lat, lng: draft.lng } : undefined}
          onText={(t) => set({ endLocation: t, endLat: null, endLng: null })}
          onPick={(r) => set({ endLocation: r.label, endLat: r.lat, endLng: r.lng })}
        />
      )}
      {isJourney(draft.type) && (
        <div className="planner__stops">
          <span className="planner__form-section">🛑 Mellanlandningar / stopp (i ordning)</span>
          {(draft.stops || []).map((s, i) => (
            <div className="planner__stop-row" key={i}>
              <LocationAutocomplete
                value={s.location}
                hasCoords={s.lat != null}
                placeholder={`Stopp ${i + 1} (autocomplete)`}
                onText={(t) =>
                  set({ stops: (draft.stops || []).map((x, j) => (j === i ? { ...x, location: t, lat: null, lng: null } : x)) })
                }
                onPick={(r) =>
                  set({ stops: (draft.stops || []).map((x, j) => (j === i ? { location: r.label, lat: r.lat, lng: r.lng } : x)) })
                }
              />
              <button
                type="button"
                className="planner__stop-del"
                title="Ta bort stopp"
                onClick={() => set({ stops: (draft.stops || []).filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="planner__stop-add"
            onClick={() => set({ stops: [...(draft.stops || []), { location: "", lat: null, lng: null }] })}
          >
            + Lägg till stopp
          </button>
        </div>
      )}
      {draft.type === "flight" && (
        <>
          <div className="planner__form-row">
            <input
              className="planner__input"
              value={draft.flightNumber || ""}
              onChange={(e) => { set({ flightNumber: e.target.value }); setFlightMsg(""); }}
              placeholder="✈️ Flightnummer (t.ex. D84365)"
            />
            <button
              type="button"
              className="planner__btn"
              disabled={flightBusy || !(draft.flightNumber || "").trim()}
              onClick={fetchFlight}
            >
              {flightBusy ? "Hämtar…" : "Hämta flyg"}
            </button>
          </div>
          {flightMsg && <span className="planner__at-hint">{flightMsg}</span>}
          <div className="planner__form-row">
            <label className="planner__field">
              <span className="planner__field-label">🛫 Avresa</span>
              <input className="planner__input" type="time" value={draft.time || ""} onChange={(e) => set({ time: e.target.value })} />
            </label>
            <label className="planner__field">
              <span className="planner__field-label">🛬 Ankomst</span>
              <input className="planner__input" type="time" value={draft.arrTime || ""} onChange={(e) => set({ arrTime: e.target.value })} />
            </label>
          </div>
        </>
      )}
      <span className="planner__form-section">Logistik (valfritt)</span>
      <div className="planner__form-row">
        <input
          className="planner__input"
          value={draft.travelTime}
          onChange={(e) => set({ travelTime: e.target.value, travelAuto: false })}
          placeholder="🚗 Restid (auto från boende)"
        />
        <select
          className="planner__select"
          value={draft.booking}
          onChange={(e) => set({ booking: e.target.value as BookingStatus })}
          title="Behöver bokas?"
        >
          {BOOKING_ORDER.map((b) => (
            <option key={b} value={b}>
              {b === "none" ? "Behöver ej bokas" : BOOKING_STATUS[b].label}
            </option>
          ))}
        </select>
      </div>
      {draft.booking !== "none" && (
        <textarea
          className="planner__textarea"
          value={draft.bookingInfo}
          onChange={(e) => set({ bookingInfo: e.target.value })}
          placeholder="🎫 Vad behöver bokas? (t.ex. parkering online, lift-biljett, tid)"
        />
      )}
      <input
        className="planner__input"
        type="number"
        min={0}
        inputMode="decimal"
        value={draft.price ? String(draft.price) : ""}
        onChange={(e) => set({ price: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value) || 0) })}
        placeholder="💰 Pris (totalt, valfritt)"
      />
      <label className="planner__check-row">
        <input
          type="checkbox"
          checked={!!draft.travelFromOther}
          onChange={(e) => set({ travelFromOther: e.target.checked })}
        />
        <span>🚗 Kommer inte resa från boendet</span>
      </label>
      {draft.travelFromOther && (
        <LocationAutocomplete
          value={draft.originLocation || ""}
          hasCoords={draft.originLat != null}
          placeholder="Startplats för resan (autocomplete)"
          onText={(t) => set({ originLocation: t, originLat: null, originLng: null })}
          onPick={(r) => set({ originLocation: r.label, originLat: r.lat, originLng: r.lng })}
        />
      )}
      <input
        className="planner__input"
        value={draft.parking}
        onChange={(e) => set({ parking: e.target.value })}
        placeholder="🅿️ Parkering (valfritt)"
      />
      <input
        className="planner__input"
        type="url"
        value={draft.link}
        onChange={(e) => { if (!tryAllTrails(e.target.value)) set({ link: e.target.value }); }}
        placeholder="🔗 Länk (AllTrails, YouTube, info…)"
      />
      {isHike && (
        <a
          className="planner__alltrails"
          href={allTrailsSearchUrl(draft.title, draft.endLocation || draft.location)}
          target="_blank"
          rel="noopener noreferrer"
        >
          🥾 Hitta vandringen på AllTrails → klistra in länken ovan
        </a>
      )}
      <textarea
        className="planner__textarea"
        value={draft.notes}
        onChange={(e) => set({ notes: e.target.value })}
        placeholder="Tips & anteckningar (valfritt)"
      />
      {multiDay ? (
        dayCount > 0 && (
          <div className="planner__daypick">
            <span className="planner__form-section">Lägg till på dagar (välj en eller flera)</span>
            <div className="planner__day-chips">
              {Array.from({ length: dayCount }, (_, i) => (
                <button
                  type="button"
                  key={i}
                  className={`planner__day-chip${days.includes(i) ? " planner__day-chip--on" : ""}`}
                  onClick={() => toggleDay(i)}
                >
                  Dag {i + 1}
                </button>
              ))}
            </div>
            <span className="planner__daypick-hint">
              {days.length === 0
                ? "Inga dagar valda → sparas som idé"
                : `Skapar ${days.length} kort (ett per vald dag)`}
            </span>
          </div>
        )
      ) : (
        showDaySelect && (
          <>
            <select
              className="planner__select"
              value={draft.day === null ? "" : String(draft.day)}
              onChange={(e) => set({ day: e.target.value === "" ? null : Number(e.target.value) })}
            >
              <option value="">Idéer (ingen dag)</option>
              {Array.from({ length: dayCount }, (_, i) => (
                <option key={i} value={i}>Dag {i + 1}</option>
              ))}
            </select>
            {draft.day != null && dayCount - draft.day > 1 && (
              <div className="planner__duration">
                <span>📆 Längd (dagar/nätter)</span>
                <input
                  type="number"
                  className="planner__input planner__duration-input"
                  min={1}
                  max={dayCount - draft.day}
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  onBlur={() => setDurationInput(String(duration))}
                />
                {duration > 1 && (
                  <span className="planner__daypick-hint">
                    Dag {draft.day + 1}–{draft.day + Math.min(duration, dayCount - draft.day)}
                  </span>
                )}
              </div>
            )}
          </>
        )
      )}
      <div className="planner__form-actions">
        <button type="button" className="planner__btn planner__btn--ghost" onClick={onCancel}>
          Avbryt
        </button>
        <button type="submit" className="planner__btn">
          {initial
            ? draft.day != null && duration > 1
              ? `Spara (${Math.min(duration, dayCount - draft.day)} dagar)`
              : "Spara"
            : multiDay
            ? days.length > 1 ? `Lägg till (${days.length})` : "Lägg till"
            : draft.day != null && duration > 1
            ? `Lägg till (${Math.min(duration, dayCount - draft.day)} dagar)`
            : "Lägg till"}
        </button>
      </div>
    </form>
  );
};
